/**
 * Post pipeline (pmndrs postprocessing): render → bloom → vignette →
 * ACES tone map → SMAA, assembled per quality tier (LOCKED-DESIGN 13.1
 * scalable visuals): 'high' is the full chain, 'medium' halves bloom,
 * 'low'/'potato' drop bloom and SMAA. Vignette and the ACES tone map are
 * part of the look, not the cost, so every tier keeps them.
 *
 * The renderer must have toneMapping = NoToneMapping (the effect does it)
 * and antialias = false (SMAA is the only AA). On a quality change the owner
 * disposes this instance and constructs a fresh one — passes are immutable.
 */
import * as THREE from "three";
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  BloomEffect,
  VignetteEffect,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  type Effect,
} from "postprocessing";
import type { QualityLevel } from "@host/types.js";
import { POST_FX_PARAMS, QUALITY_TIERS } from "./theme.js";

/**
 * True when a proposed size may safely reach EffectComposer.setSize.
 *
 * Non-finite dimensions MUST NOT enter the composer: every pass Resolution
 * guards its change event with `baseSize.width !== width`, which is
 * PERMANENTLY true for NaN (NaN !== NaN), so the BloomEffect resolution
 * listener re-enters BloomEffect.setSize -> Resolution.setBaseSize ->
 * dispatch("change") forever — RangeError: maximum call stack size exceeded.
 * (Reproduced live via __tpz.resize({width,height}) with a missing dpr: the
 * NaN pixel ratio poisons renderer.getDrawingBufferSize, which composer
 * .setSize forwards to every pass.) Zero/negative sizes are rejected too —
 * render targets degenerate at < 1px.
 */
export function isSafeRenderSize(width: number, height: number): boolean {
  return Number.isFinite(width) && Number.isFinite(height) && width >= 1 && height >= 1;
}

export class PostFx {
  private readonly composer: EffectComposer;
  private primed = false;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    width: number,
    height: number,
    quality: QualityLevel = "high",
  ) {
    const tier = QUALITY_TIERS[quality];
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
    });
    this.composer.addPass(new RenderPass(scene, camera));
    const effects: Effect[] = [];
    if (tier.bloomScale > 0) {
      effects.push(new BloomEffect({ ...POST_FX_PARAMS.bloom, intensity: POST_FX_PARAMS.bloom.intensity * tier.bloomScale }));
    }
    effects.push(new VignetteEffect({ ...POST_FX_PARAMS.vignette }));
    effects.push(new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC }));
    if (tier.smaa) effects.push(new SMAAEffect());
    this.composer.addPass(new EffectPass(camera, ...effects));
    this.assertSafeSize(width, height);
    this.composer.setSize(width, height);
    this.cameraRef = camera;
  }

  /** Loud contract at the seam (see isSafeRenderSize) instead of a library-deep stack overflow. */
  private assertSafeSize(width: number, height: number): void {
    if (!isSafeRenderSize(width, height)) {
      throw new TypeError(
        `PostFx requires finite dimensions >= 1 (got ${width}x${height}); ` +
          "non-finite sizes recurse forever in the postprocessing Resolution change listeners",
      );
    }
  }

  private readonly cameraRef: THREE.PerspectiveCamera;

  render(dt: number): void {
    if (!this.primed) {
      // one direct render before the composer's first frame: a cold renderer
      // drawing straight into half-float targets reads far too dark (hard-won
      // lesson from the previous project's black-void bug)
      this.renderer.render(this.scene, this.cameraRef);
      this.primed = true;
    }
    this.composer.render(dt);
  }

  resize(width: number, height: number): void {
    this.assertSafeSize(width, height);
    this.composer.setSize(width, height);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
